import { api } from "../../utils/api";

export const getReadableAddress = (fields) => Object.values(fields).join(", ");

let googleMapsScriptPromise;

const formatStoreAddress = (address = {}) => {
  const { street, city, state, postcode, country } = address;
  const countryText =
    typeof country === "string" ? country : country?.label || country?.name || country?.value || "";
  return [street, city, state, postcode, countryText].filter((part) => part && String(part).trim()).join(", ");
};

const hasSavedCoordinates = (coordinates) =>
  Number.isFinite(Number(coordinates?.lat)) && Number.isFinite(Number(coordinates?.lng));

const toCoordinates = (coordinates) => ({
  lat: Number(coordinates.lat),
  lng: Number(coordinates.lng)
});

const saveStoreCoordinates = async (storeId, coordinates) => {
  const response = await api(`v1/merchantStore/${storeId}/coordinates`, "PATCH", {
    lat: coordinates.lat,
    lng: coordinates.lng
  });
  if (!response?.data?.success) {
    console.warn("[P1 Map] Could not save store coordinates", storeId, response?.data?.message);
  }
};

/**
 * Loads the Maps JavaScript API. Pin coordinates must use google.maps.Geocoder.
 * The Geocoding REST endpoint (/maps/api/geocode/json) rejects keys that have
 * HTTP referrer restrictions — the restriction this browser key is meant to use.
 */
export const loadGoogleMapsApi = ({ places = false } = {}) => {
  if (typeof window === "undefined") return Promise.resolve(false);

  const hasGeocoder = () => Boolean(window.google?.maps?.Geocoder);
  const hasPlaces = () => Boolean(window.google?.maps?.places);

  const ensurePlaces = async (loaded) => {
    if (!loaded) return false;
    if (!places || hasPlaces()) return true;
    if (typeof window.google.maps.importLibrary !== "function") return false;
    try {
      await window.google.maps.importLibrary("places");
      return hasPlaces();
    } catch (error) {
      console.warn("[P1 Map] Places library failed to load", error);
      return false;
    }
  };

  if (!googleMapsScriptPromise) {
    googleMapsScriptPromise = new Promise((resolve) => {
      if (hasGeocoder()) {
        resolve(true);
        return;
      }

      const key = process.env.REACT_APP_PUBLIC_GOOGLE_MAPS_API_KEY;
      if (!key || !key.trim()) {
        resolve(false);
        return;
      }

      const finish = (loaded) => resolve(loaded);
      const existing = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
      if (existing) {
        const started = Date.now();
        const poll = () => {
          if (hasGeocoder()) {
            finish(true);
            return;
          }
          if (Date.now() - started > 10000) {
            finish(false);
            return;
          }
          setTimeout(poll, 50);
        };
        existing.addEventListener("error", () => finish(false), { once: true });
        poll();
        return;
      }

      const script = document.createElement("script");
      script.id = "p1-google-maps-script";
      script.async = true;
      script.defer = true;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key.trim())}&libraries=places`;
      script.onload = () => finish(hasGeocoder());
      script.onerror = () => resolve(false);
      document.head.appendChild(script);
    }).then((loaded) => {
      if (!loaded) googleMapsScriptPromise = null;
      return loaded;
    });
  }

  return googleMapsScriptPromise.then((loaded) => ensurePlaces(loaded));
};

const geocodeOnce = (address) =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve({ ok: false, status: "TIMEOUT" }), 8000);
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address }, (results, status) => {
      clearTimeout(timer);
      if (status === "OK" && results?.[0]?.geometry?.location) {
        const location = results[0].geometry.location;
        resolve({
          ok: true,
          coordinates: {
            lat: typeof location.lat === "function" ? location.lat() : location.lat,
            lng: typeof location.lng === "function" ? location.lng() : location.lng
          }
        });
        return;
      }
      resolve({ ok: false, status });
    });
  });

const geocodeAddress = async (address) => {
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const result = await geocodeOnce(address);
    if (result.ok) return result.coordinates;
    if (result.status !== "OVER_QUERY_LIMIT" && result.status !== "UNKNOWN_ERROR") {
      console.warn("[P1 Map] Geocode failed", result.status, address);
      return null;
    }
    await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
  }
  console.warn("[P1 Map] Geocode failed after retries", address);
  return null;
};

const mapPool = async (items, limit, worker) => {
  const results = new Array(items.length);
  let nextIndex = 0;

  const run = async () => {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await worker(items[index]);
    }
  };

  const workers = Math.min(limit, items.length);
  if (workers > 0) {
    await Promise.all(Array.from({ length: workers }, () => run()));
  }
  return results;
};

export const accessUsersLocation = (success, error) => {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(success, error);
  } else {
    console.log("Geolocation not supported");
  }
};

export const getAllAvailableProducts = (merchants) => {
  const uniqueProducts = {}; // Using an object for efficient lookup
  const result = [];

  if (merchants) {
    merchants.forEach((merchant) => {
      if (merchant.products && merchant.products.length > 0) {
        merchant.products.forEach((product) => {
          if (product && product.value && !uniqueProducts[product.value.value]) {
            uniqueProducts[product.value.value] = true;
            result.push(product);
          }
        });
      }
    });
  }
  return result;
};

export const matchSearchToMerchant = (search, merchant) => {
  const lowercasedSearch = search.toLowerCase();
  return (
    merchant.products.some((product) => product?.label && product?.label.toLowerCase().includes(lowercasedSearch)) ||
    merchant.name.toLowerCase().includes(lowercasedSearch) ||
    getReadableAddress(merchant.address).toLowerCase().includes(lowercasedSearch)
  );
};

export const matchMerchantProducts = (merchant, productList) =>
  merchant.products.some((product) => product && productList.some((p) => p.value == product.value.value));

export const getMerchantsWithCoordinates = async (merchants) => {
  const list = merchants || [];
  const needsGeocode = list.some((merchant) => !hasSavedCoordinates(merchant?.coordinates));
  if (needsGeocode) {
    const ready = await loadGoogleMapsApi();
    if (!ready) {
      console.warn("[P1 Map] Google Maps JavaScript API did not load; stores without saved coordinates were skipped.");
    }
  }

  const result = await mapPool(list, 4, async (merchant) => {
    if (hasSavedCoordinates(merchant?.coordinates)) {
      return { ...merchant, coordinates: toCoordinates(merchant.coordinates) };
    }

    if (!window.google?.maps?.Geocoder) return null;

    const address = formatStoreAddress(merchant?.address);
    if (!address) return null;

    const coordinates = await geocodeAddress(address);
    if (!coordinates) return null;

    try {
      await saveStoreCoordinates(merchant._id, coordinates);
    } catch (error) {
      console.warn("[P1 Map] Could not save store coordinates", merchant._id, error);
    }
    return { ...merchant, coordinates };
  });

  return result.filter(Boolean);
};

export const groupMerchantStoresByAccount = (merchants) =>
  merchants.reduce((prev, curr) => {
    if (prev.hasOwnProperty(curr.merchantAccount)) {
      return { ...prev, [curr.merchantAccount]: [...prev[curr.merchantAccount], curr._id] };
    } else {
      return { ...prev, [curr.merchantAccount]: [curr._id] };
    }
  }, {});

export const getMerchantStoreAccountNames = async (merchants) => {
  const result = await Promise.all(
    merchants.map(async (merchant) => {
      const response = await api(`v1/merchant/${merchant.merchantAccount}`, "GET");
      if (response.data.success) {
        return {
          ...merchant,
          merchantAccountName: response.data.data.merchant.name
        };
      } else {
        return { ...merchant };
      }
    })
  );
  return result;
};

export const getMerchantStoresWithProducts = async (merchants) => {
  const result = await Promise.all(
    merchants.map(async (merchant) => {
      const result = await Promise.all(
        merchant.products.map(async (p) => {
          if (p.value) {
            const resp = await api(`v1/product/${p.value}`);
            return resp.data.success
              ? {
                  value: p,
                  label: resp.data.data.product.name
                }
              : null;
          }
        })
      );
      return {
        ...merchant,
        products: result
      };
    })
  );
  return result;
};
